import { useSelector } from "react-redux";
import {
  IDeviationProduct,
  selectDeviationProducts,
  useBillingQuery,
  useCreateOrUpdateDeviationsByBasketIdsMutation,
  useValidateBasketMutation,
} from "@chef/state-management";
import {
  getBasketFinances,
  getWeeklyTimeblockDataFromCalendar,
} from "@chef/state-management/helpers";

import { isGroceryBasketType, isGroceryProduct } from "@chef/helpers";
import { useLoyalty } from "@chef/state-management/hooks";
import { sum } from "@chef/utils/array";

import { useCalendarData, useTrack } from "@chef/state-management/hooks";

export const useSubmitDeviationChange = ({
  week,
  year,
}: {
  week: number;
  year: number;
}) => {
  const track = useTrack();

  const { calendar, pickAndMix } = useCalendarData({
    week,
    year,
  });

  const { data: billingData } = useBillingQuery();

  const [validateBasketMutation] = useValidateBasketMutation();

  const groceryBasketId =
    billingData?.billing.baskets.find(isGroceryBasketType)?.basketId;

  const [
    createOrUpdateDeviationsByBasketIds,
    { isLoading: isSubmittingDeviation },
  ] = useCreateOrUpdateDeviationsByBasketIdsMutation({
    fixedCacheKey: "quick-add-deviation-change",
  });

  const { data: loyaltyData } = useLoyalty();

  const selectedProducts = useSelector(selectDeviationProducts({ week, year }));

  const timeblock =
    calendar &&
    getWeeklyTimeblockDataFromCalendar({
      calendar,
      week,
      year,
    });

  const finances =
    selectedProducts &&
    getBasketFinances({
      basketProducts: selectedProducts,
      customerFee: timeblock?.customerFee,
      loyaltyLevel: loyaltyData?.currentLevel,
    });

  const submitDeviation = async (selectedProducts: IDeviationProduct[]) => {
    if (!selectedProducts || !pickAndMix || !groceryBasketId) {
      throw new Error("Data not initialized");
    }

    await validateBasketMutation(
      selectedProducts.map((bp) => ({
        quantity: bp.quantity,
        variationId: bp.variationId,
      })),
    ).unwrap();

    await createOrUpdateDeviationsByBasketIds({
      week,
      year,
      baskets: [
        {
          basketId: groceryBasketId,
          products: selectedProducts.filter(isGroceryProduct).map((bp) => ({
            variationId: bp.variationId,
            quantity: bp.quantity,
          })),
        },
      ],
    }).unwrap();

    track("deviationCreated", {
      affiliation: "Frontend process",
      source: "quick_add",
      is_active: true,
      products: selectedProducts.map((bp) => ({
        product_id: bp.productId,
        variant_id: bp.variationId,
        quantity: bp.quantity,
        price: bp.price,
        category_id: bp.productTypeId,
      })),
      quantity: sum(selectedProducts.map((bp) => bp.quantity)),
      total: finances?.total,
      status: billingData?.billing.status,
      week,
      year,
    });
  };

  return {
    submitDeviation,
    isSubmittingDeviation,
  };
};
